import React, { useState, useEffect, useCallback } from 'react';
import {
  TitleRow,
  ContainerRow,
  SearchInputStyle,
  ControlsDiV
} from '../styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import { useSelector } from 'react-redux';
import PageTitle from '../../../Utils/PageTitle';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import {
  SEARCH_DELAY
} from '../../../Constants/StringConstants';
import { createScribeColumns } from './tanstackColumnConfig';
import AddScribesForm from './AddScribesForm';
import CustomSideDrawer from '../../CustomComponents/CustomSideDrawer';

import { notification } from 'antd';
import { SearchOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDebounce } from '../../../Utils/Debounce';
import { AddButton } from '../styledComponents';
import { exportToExcel } from '../../../Utils/ExcelExport';
import { reInviteUser } from '../../../Apis/User';
import { disableOrEnableUser } from '../../../Apis/ClinicAdmin';
import { AddScribe, listAll, editScribe } from '../../../Apis/Scribe';

const Scribes = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [searchString, setSearchString] = useState('');
  const [scribes, setScribes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const userData = useSelector(state => state.userReducer);
  const notClinicAdmin = userData.userType !== 'clinicAdmin' && userData.userType !== 'admin';

  const submitHandler = values => {
    setSubmitLoading(true);
    const payload = values;
    values.phoneNumber.number = values.phoneNumber.number.replace(
      values.phoneNumber.countryCode,
      ''
    );
    if (selectedItem) {
      payload.id = selectedItem.id;
      editScribe(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Updated',
              description: 'Scribe details updated.'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    } else {
      AddScribe(payload)
        .then(response => {
          if (response.data.success) {
            notification['success']({
              message: 'Added',
              description: 'Scribe added successfully'
            });
            setSubmitLoading(false);
            setAddDrawerVisible(false);
            setSelectedItem(null);
            fetchAll();
          } else {
            notification['error']({
              message: 'Failed',
              description: response.data.message
            });
            setSubmitLoading(false);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          console.log(err);
        });
    }
  };

  const reInvite = id => {
    reInviteUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const disableUser = id => {
    disableOrEnableUser(id)
      .then(response => {
        if (response.data.success) {
          notification['success']({
            message: 'Success',
            description: response.data.message
          });
          fetchAll();
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const fetchAll = () => {
    setLoading(true);
    listAll()
      .then(response => {
        setLoading(false);
        if (response.data?.success) {
          setScribes(response.data.result || []);
        } else if (Array.isArray(response.data)) {
          setScribes(response.data);
        }
      })
      .catch(err => {
        setLoading(false);
        notification['error']({
          message: 'Failed',
          description: 'Could not load scribes'
        });
      });
  };

  const debounceOnChange = useCallback(
    useDebounce(value => {
      setSearchString(value);
    }, SEARCH_DELAY),
    []
  );

  const handleExport = () => {
    let filteredData = scribes;

    if (searchString) {
      const searchLower = searchString.toLowerCase();
      filteredData = filteredData.filter(scribe => {
        return (
          (scribe.fullName && scribe.fullName.toLowerCase().includes(searchLower)) ||
          (scribe.email && scribe.email.toLowerCase().includes(searchLower)) ||
          (scribe.phoneNumber &&
            `${scribe.phoneNumber.countryCode}${scribe.phoneNumber.number}`.includes(searchLower))
        );
      });
    }

    const exportColumns = [
      { id: 'id', header: 'ID', accessorKey: 'id' },
      { id: 'fullName', header: 'Name', accessorKey: 'fullName' },
      { id: 'email', header: 'Email', accessorKey: 'email' },
      {
        id: 'assignedDoctor',
        header: 'Assigned Doctor',
        accessorFn: row => row.assignedDoctor?.doctorName || ''
      },
      { id: 'phoneNumber', header: 'Phone Number', accessorKey: 'phoneNumber' }
    ];

    exportToExcel(filteredData, exportColumns, 'Scribes');
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageTitle title='Scribes' />
      <ContainerRow gutter={[0, 40]}>
        <TitleRow justify='space-between'>
          <CustomTypography color={theme['@text-color']}>
            Scribes
          </CustomTypography>
          <ControlsDiV>
            <AddButton
              icon={<PlusOutlined />}
              type='primary'
              onClick={() => setAddDrawerVisible(true)}
            >
              Add
            </AddButton>
            <SearchInputStyle
              placeholder='Search'
              prefix={<SearchOutlined />}
              value={searchString}
              onChange={event => {
                setSearchString(event.target.value);
                debounceOnChange(event.target.value);
              }}
            />
            <AddButton
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              Export to Excel
            </AddButton>
            <AddButton
              onClick={() => {
                setSearchString('');
                setClearFiltersTrigger(t => t + 1);
              }}
            >
              Clear Filter
            </AddButton>
          </ControlsDiV>
        </TitleRow>
        <TanStackUserTable
          data={scribes}
          columns={createScribeColumns(
            setSelectedItem,
            setAddDrawerVisible,
            disableUser,
            reInvite,
            notClinicAdmin
          )}
          loading={loading}
          searchValue={searchString}
          storageKey='users_page_scribes'
          clearFiltersTrigger={clearFiltersTrigger}
        />

        <CustomSideDrawer
          title={selectedItem ? 'Edit Scribe' : 'Add Scribe'}
          placement='right'
          closable={false}
          onClose={() => {
            setSelectedItem(null);
            setAddDrawerVisible(false);
          }}
          visible={addDrawerVisible}
          width={400}
          destroyOnClose={true}
        >
          <AddScribesForm
            selectedItem={selectedItem}
            submitHandler={submitHandler}
            submitLoading={submitLoading}
          />
        </CustomSideDrawer>
      </ContainerRow>
    </>
  );
};

export default Scribes;
